<template>
  <v-tooltip v-if="isImportedTemplate" bottom>
    <template v-slot:activator="{ on, attrs }">
      <div v-bind="attrs" v-on="on">
        <v-btn :disabled="!canReimport" icon @click="reimportData()" :loading="reimportLoading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </template>
    <span>{{ toolTipText }}</span>
  </v-tooltip>
</template>

<script lang="ts">
  import ClientService from '@/services/client/client';
  import {
    PageBlock,
    PlayerDetails,
    RosterBlockData,
    ScheduleBlockData,
    ScheduleEvents,
    ScheduleEventsGetters,
  } from '@/store/client/types';
  import { PromiseAllChunked } from '@/utils/apiHelpers';
  import { sortBy } from 'lodash-es';
  import Vue from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  export default Vue.extend({
    name: 'PageReimportButton',
    data() {
      return {
        reimportLoading: false,
      };
    },
    computed: {
      ...mapGetters('builder', { page: 'getPage' }),
      ...mapGetters('programs', { program: 'getProgram' }),
      programId(): number {
        return this.program.id;
      },
      isImportedTemplate(): boolean {
        return !this.page.templateId;
      },
      pageType(): string {
        const { blocks }: { blocks: PageBlock[] } = this.page.content;
        const blockTypes = new Set(['schedule', 'roster']);
        for (const block of blocks) {
          if (blockTypes.has(block.type)) {
            return block.type;
          }
        }

        return '';
      },
      canReimport(): boolean {
        const blocks: PageBlock[] = this.page.content.blocks;
        switch (this.pageType) {
          case 'schedule': {
            const { isOpponent, upcoming, results } = blocks.find((b) => b.type === 'schedule')
              .data as ScheduleBlockData;
            if (isOpponent == undefined) {
              return false;
            }
            for (const e of [...upcoming, ...results]) {
              if (e.gameId === undefined) {
                return false;
              }
            }
            break;
          }
          case 'roster': {
            const { isOpponent } = blocks.find((b) => b.type === 'roster').data as RosterBlockData;
            if (isOpponent == undefined) {
              return false;
            }
            break;
          }
          default: {
            return false;
          }
        }

        return true;
      },
      toolTipText(): string {
        return this.canReimport ? 'Refresh Imported Page Data' : 'Refresh Unavailable';
      },
    },
    methods: {
      ...mapActions('builder', {
        savePage: 'savePage',
      }),
      ...mapActions('ui', { showToast: 'showToast' }),
      fetchClient: ClientService.fetchClient,
      fetchScheduleResultsEvents: ClientService.fetchScheduleResultsEvents,
      fetchScheduleUpcomingEvents: ClientService.fetchScheduleUpcomingEvents,
      fetchPlayers: ClientService.fetchPlayers,
      getOpponentTenantId: ClientService.getOpponentTenantId,
      fetchOpponentRosterBio: ClientService.fetchOpponentRosterBio,
      fetchOpponentScheduleResultEvents: ClientService.fetchOpponentScheduleResultEvents,
      fetchOpponentScheduleUpcomingEvents: ClientService.fetchOpponentScheduleUpcomingEvents,
      fetchGlobalSport: ClientService.fetchGlobalSport,
      async reimportSchedule() {
        try {
          const {
            clientId,
            sport: { clientSportId, globalSportId },
          } = this.program;

          const gameIds: string[] = [];

          const { results, upcoming, isOpponent, globalId }: ScheduleBlockData = this.page?.content?.blocks[2]?.data;

          [...upcoming, ...results].forEach((e) => {
            if (e.gameId) {
              gameIds.push(e.gameId);
            }
          });

          const updatedPage = JSON.parse(JSON.stringify(this.page));

          //keep all events with empty gameId since they were manually added
          const newUpcoming = upcoming.filter((e) => e.gameId === '');
          const newResults = results.filter((e) => e.gameId === '');

          if (!isOpponent) {
            const client = await this.fetchClient(clientId);

            let upcomingEvents = await this.fetchScheduleUpcomingEvents(
              { clientId, clientSportId, hardFetch: true },
              false
            );

            let resultEvents = await this.fetchScheduleResultsEvents(
              { clientId, clientSportId, hardFetch: true },
              false
            );

            resultEvents.forEach((item) => {
              if (!item.gameId || !gameIds.includes(item.gameId)) {
                return;
              }

              if (item.opponent.logo !== '' && !/https?/.test(item.opponent.logo)) {
                item.opponent.logo = `${client.siteUrl}${item.opponent.logo}`;
              }

              newResults.push({
                ...item,
                opponentLogo: item.opponent.logo,
                opponent: item.opponent.name,
              });
            });

            upcomingEvents.forEach((item) => {
              if (!item.gameId || !gameIds.includes(item.gameId)) {
                return;
              }

              if (item.opponent.logo !== '' && !/https?/.test(item.opponent.logo)) {
                item.opponent.logo = `${client.siteUrl}${item.opponent.logo}`;
              }

              newUpcoming.push({
                ...item,
                opponentLogo: item.opponent.logo,
                opponent: item.opponent.name,
              });
            });
          } else {
            let opponentGlobal = await this.getOpponentTenantId(globalId);

            let opponentTenantId = opponentGlobal.client_abbreviation;
            let opponentSiteUrl = opponentGlobal.website;

            const schoolSport = await this.fetchGlobalSport({
              globalSportId,
              tenantId: opponentTenantId,
            });
            const sportId = schoolSport.id;

            let upcomingEvents: ScheduleEventsGetters[] = await this.fetchOpponentScheduleUpcomingEvents({
              sportId,
              tenantId: opponentTenantId,
              hardFetch: true,
            });

            let resultEvents: ScheduleEventsGetters[] = await this.fetchOpponentScheduleResultEvents({
              sportId,
              tenantId: opponentTenantId,
              hardFetch: true,
            });

            resultEvents.forEach((item) => {
              if (!item.gameId || !gameIds.includes(item.gameId)) {
                return;
              }

              if (item.opponent?.image !== '' && !/https?/.test(item.opponent?.image)) {
                item.opponent.image = `${opponentSiteUrl}${item.opponent?.image}`;
              }

              newResults.push({
                gameId: item.gameId,
                date: item.date,
                location: item.location,
                locationIndicator: item.locationIndicator,
                result: `${item.result?.status} ${item.result?.teamScore}-${item.result?.opponentScore}`,
                stadiumName: item.stadiumName ? item.stadiumName : '',
                time: item.time,
                opponentLogo: item.opponent?.image,
                opponent: item.opponent?.title,
              });
            });

            upcomingEvents.forEach((item) => {
              if (!item.gameId || !gameIds.includes(item.gameId)) {
                return;
              }

              if (item.opponent?.image !== '' && !/https?/.test(item.opponent?.image)) {
                item.opponent.image = `${opponentSiteUrl}${item.opponent?.image}`;
              }

              newUpcoming.push({
                gameId: item.gameId,
                date: item.date,
                location: item.location,
                locationIndicator: item.locationIndicator,
                stadiumName: item.stadiumName ? item.stadiumName : '',
                time: item.time,
                opponentLogo: item.opponent?.image,
                opponent: item.opponent?.title,
              });
            });
          }

          updatedPage.content.blocks[2].data = {
            ...updatedPage.content.blocks[2].data,
            results: newResults.sort((e1, e2) => new Date(e1.date).getTime() - new Date(e2.date).getTime()),
            upcoming: newUpcoming.sort((e1, e2) => new Date(e1.date).getTime() - new Date(e2.date).getTime()),
          };

          await this.savePage({
            programId: this.programId,
            page: updatedPage,
          });
        } catch (error) {
          const msg = error?.response?.data;
          if (msg) {
            console.error(msg);
          }
          throw error;
        }
      },
      async reimportRoster() {
        try {
          const { clientId } = this.program;
          const updatedPage = JSON.parse(JSON.stringify(this.page));
          const { isOpponent, globalId, players }: RosterBlockData = updatedPage.content.blocks[2].data;

          const playersIds: number[] = [];
          players.forEach(({ clientRosterPlayerId }) => {
            if (clientRosterPlayerId) {
              playersIds.push(clientRosterPlayerId);
            }
          });
          //keep players with 0 ids since those were manually added
          const newPlayers = players.filter((p) => p.clientRosterPlayerId === 0);

          if (!isOpponent) {
            const client = await this.fetchClient(clientId);
            const teamInfo = this.program.event.home;

            //Consider changing the following endpoint to a POST request instead of a GET so we can
            //freely pass a large array of values and not worry about hitting a ~2KB url character limit
            const tasks: (() => Promise<Record<number, PlayerDetails>>)[] = [];
            playersIds.forEach((id) =>
              tasks.push(() => this.fetchPlayers({ clientId, playersIds: [id], hardFetch: true }))
            );
            const results = await PromiseAllChunked(tasks);
            var playersMap: Record<number, PlayerDetails> = {};
            results.forEach((kvPair) => {
              playersMap = {
                ...playersMap,
                ...kvPair,
              };
            });

            Object.values(playersMap).forEach((p) => {
              if (!p) return;

              if (p.rosterHeadshot !== '' && !/https?/.test(p.rosterHeadshot)) {
                p.rosterHeadshot = `${client.siteUrl}${p.rosterHeadshot}`;
              }
              if (!p.rosterHeadshot.includes('images')) {
                p.rosterHeadshot = teamInfo.logoUrl;
              }
              newPlayers.push(p);
            });
          } else {
            var opponentGlobal = await this.getOpponentTenantId(globalId);

            var opponentTenantId = opponentGlobal.client_abbreviation;
            var opponentSiteUrl = opponentGlobal.website;

            //Consider changing the following endpoint to a POST request instead of a GET so we can
            //freely pass a large array of values and not worry about hitting a ~2KB url character limit
            const tasks: (() => Promise<any>)[] = [];
            playersIds.forEach((id) =>
              tasks.push(() =>
                this.fetchOpponentRosterBio({ tenantId: opponentTenantId, rosterPlayerId: id, hardFetch: true })
              )
            );

            const playerBios = await PromiseAllChunked(tasks);

            playerBios.forEach((playerBio) => {
              if (!playerBio) return;

              if (
                playerBio.image &&
                playerBio.image.absoluteUrl !== '' &&
                !/https?/.test(playerBio.image.absoluteUrl)
              ) {
                playerBio.image.absoluteUrl = `${opponentSiteUrl}${playerBio.image.absoluteUrl}`;
              }

              newPlayers.push({
                bio: playerBio.bio,
                class: playerBio.academicYearLong,
                position: playerBio.positionShort,
                height: `${playerBio.heightFeet}′${playerBio.heightInches}″`,
                weight: playerBio.weight,
                hometown: playerBio.hometown,
                firstName: playerBio.firstName,
                lastName: playerBio.lastName,
                isCaptain: playerBio.isCaptain,
                actionShot: playerBio.image?.absoluteUrl ? playerBio.image?.absoluteUrl : '',
                highSchool: playerBio.highSchool,
                jerseyNumber: playerBio.jerseyNumber,
                previousSchool: playerBio.previousSchool,
                rosterHeadshot: playerBio.image?.absoluteUrl ? playerBio.image?.absoluteUrl : '',
                actionShotAltText: playerBio.image?.altText,
                rosterHeadshotAltText: playerBio.image?.altText,
                clientRosterPlayerId: playerBio.rosterPlayerId,
              });
            });
          }

          updatedPage.content.blocks[2].data.players = sortBy(newPlayers, ['lastName', 'firstName']);
          await this.savePage({
            programId: this.programId,
            page: updatedPage,
          });
        } catch (error) {
          const msg = error?.response?.data;
          if (msg) {
            console.error(msg);
          }
          throw error;
        }
      },
      async reimportData() {
        try {
          if (!this.canReimport) {
            return;
          }

          this.reimportLoading = true;

          switch (this.pageType) {
            case 'schedule':
              await this.reimportSchedule();
              break;
            case 'roster':
              await this.reimportRoster();
              break;
          }

          this.showToast('Successfully refreshed imported data!');
        } catch (error) {
          this.showToast('Failed to refresh imported data');
        } finally {
          this.reimportLoading = false;
        }
      },
    },
  });
</script>
